import React, { useEffect, useMemo, useState } from 'react';
import 'jspdf-autotable';
import DatePicker from 'react-datepicker';
import { toast, ToastContainer } from 'react-toastify';
// components
import Loader from '../Components/Loader';
import { Search } from '../Components/Search';
// redux
import { fetchServices } from '../slice/carriersSlice';
import { useDispatch, useSelector } from 'react-redux';
//mui
import { Typography } from '@mui/material';
import { gridStyles, heading, toggleButtonStyles } from '../CSS/mui-css/carriers';
import { widthFull } from '../CSS/mui-css/common';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
// utils
import {
  carrierVersionColumns,
  generatePDF,
  getCarrierRows,
  getCarrierVersionRows,
  allCarrierColumns,
  calculateTotalsForCarrierVersions,
} from '../utils/dashbaord';
import { filter } from '../utils/filter';
// css
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import {  allCarrierSearchColumnsText, carrierVersionSearchColumnsText, searchColumns } from '../constants';

export default function Carriers() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [alignment, setAlignment] = useState('Carriers');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredRows, setFilteredRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [finalPdfLoading, setFinalPdfLoading] = useState(false)

  const { services = [], loading = false } = useSelector((state) => state.carriers || {});

  const token = useSelector(state => state.user.user.token)
  const userRole = useSelector((state) => state.user.userRole);
  const updated =true;

  const handleChange = (event, newAlignment) => {
    let changeToggle;
    if (newAlignment === null) {
      changeToggle = alignment;
    } else {
      changeToggle = newAlignment;
      setSearchQuery("");
    }
    setAlignment(changeToggle);
  };

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    if (start && end) {
      const dayDifference = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      if (dayDifference > 15) {
        setStartDate(null);
        toast.error('You can only select a date range of up to 15 days.', {
          position: 'top-right',
          autoClose: 5000,
        });
        return;
      }
    }
    setStartDate(start);
    setEndDate(end);
  };

  const isEndDateToday = endDate?.toDateString() === new Date().toDateString();

  if (alignment !== "Carriers") {
    searchColumns.push("shipmentDate");
  }

  const carriers = useMemo(() => getCarrierRows(services, userRole), [services, userRole]);
  const allCarrierVersionsRows = useMemo(() => getCarrierVersionRows(services, userRole), [services, userRole]);

  const rows = useMemo(() => {
    return alignment === "Carriers" ? carriers : allCarrierVersionsRows;
  }, [alignment, carriers, allCarrierVersionsRows]);

  useEffect(() => {
    setFilteredRows((prev) => {
      const totalRow = rows.find(row => row.id === 'Total');
      const dataRows = rows.filter(row => row.id !== 'Total');
  
      const newRows = filter(dataRows, searchQuery, searchColumns);
  
      const finalRows = totalRow ? [...newRows, totalRow] : newRows;
  
      return JSON.stringify(prev) === JSON.stringify(finalRows) ? prev : finalRows;
    });
  }, [searchQuery, rows]);
  

  const columns = alignment === 'Carriers'
    ? allCarrierColumns(userRole, theme).map(column => ({
      ...column,
      flex: column.flex || 1,
    }))
    : carrierVersionColumns(userRole);
  
  const handleGeneratePDF = async () => {
    setIsLoading(true);
    await generatePDF({ startDate, endDate, token });
    setIsLoading(false);
  };

  const handleGenerateUpdatedPDF = async () => {
    setFinalPdfLoading(true);
    await generatePDF({ startDate, endDate, token, updated });
    setFinalPdfLoading(false);
  };

  const pageHeading = alignment === 'Carriers'
    ? 'Carriers'
    : 'Carrier Versions';

  const finalRows = calculateTotalsForCarrierVersions(filteredRows)

  const searchBy = alignment === "Carriers"
    ? allCarrierSearchColumnsText
    : carrierVersionSearchColumnsText;

  if (loading) return <Loader />
  return (
    <Box sx={widthFull}>
      <div className="shadow-md p-1 bg-white rounded-xl border border-gray-100 mb-3 mt-3">
        <Typography sx={heading}>{pageHeading}</Typography>
      </div>
      <ToastContainer />

      <div className='flex flex-row justify-between items-center'>
        <Search
          setSearchQuery={setSearchQuery}
          searchQuery={searchQuery}
          searchBy={searchBy}
        />

        <div className='flex mb-2  items-center space-x-4 '>
          <div className={isEndDateToday ? '' : 'custom-datepicker'}>
            <div className='relative'>
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                selectsRange
                placeholderText="Select date range"
                className=" border rounded px-3 py-1 pl-8 "
                inputProps={{
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />
              <CalendarMonthIcon
                className="absolute top-1/2 left-2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>

          <button
            onClick={handleGeneratePDF}
            disabled = {isLoading}
            className={`min-w-[125px] h-8 bg-white rounded cursor-pointer transition-all duration-300 text-sm px-5`}
            style={{
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.palette.primary.main;
              e.target.style.color = 'white';
              e.target.style.border = 'none';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = theme.palette.primary.main;
                e.target.style.border = `1px solid ${theme.palette.primary.main}`;
              }
            }}
          >
            {
              isLoading
                ? <Loader size={15} inline hover />
                : "Generate Draft PDF"
            }
          </button>
          <button
            onClick={handleGenerateUpdatedPDF}
            disabled = {finalPdfLoading}
            className={`min-w-[125px] h-8 bg-white  mt-1.3 rounded cursor-pointer transition-all duration-300 text-sm px-5`}
            style={{
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.palette.primary.main;
              e.target.style.color = 'white';
              e.target.style.border = 'none';
            }}
            onMouseLeave={(e) => {
              if (!finalPdfLoading) {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = theme.palette.primary.main;
                e.target.style.border = `1px solid ${theme.palette.primary.main}`;
              }
            }}
          >
            {
              finalPdfLoading
                ? <Loader size={15} inline hover />
                : "Generate Final PDF"
            }
          </button>
        </div>

        <ToggleButtonGroup
          value={alignment}
          size="small"
          exclusive
          onChange={handleChange}
          aria-label="Platform"
          className="toggle-button"
          sx={{
            ...toggleButtonStyles(theme.palette.primary.main),
            '& .MuiButtonBase-root.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
            },
            '& .MuiButtonBase-root': {
              borderRadius: 0,
            },
            '& .MuiButtonBase-root.Mui-selected:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <ToggleButton value="Carriers" fontSize={5}>Carriers</ToggleButton>
          <ToggleButton value="All Carriers">Carrier Versions</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <Box sx={gridStyles}>
        <DataGrid
          key={alignment}
          rows={alignment === "Carriers" ? filteredRows ? filteredRows : [] : finalRows}
          columns={columns}
          getRowClassName={((params) =>
            params?.row?.name === 'All Carriers' ||  params?.row?.name === '' ? 'bg-gray-200 font-bold ' : '')}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: alignment === 'Carriers' ? 10 : 8,
              },
            },
          }}
          pageSizeOptions={[10]}
        />
      </Box>
    </Box>
  )
}
